import {useState, useEffect, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ChevronLeft} from 'lucide-react';
import {createMutationErrorHandler, trpc, type RouterOutput} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {StepIndicator} from '@common-web/components/ui/step-indicator';
import {StepAgentLookup} from '@internal/components/group-build-wizard/StepAgentLookup';
import {StepGroupInfo} from '@internal/components/group-build-wizard/StepGroupInfo';
import {StepProductTemplate} from '@internal/components/group-build-wizard/StepProductTemplate';
import {StepReview} from '@internal/components/group-build-wizard/StepReview';
import {WizardState} from '@internal/components/group-build-wizard/types';
import {
  WIZARD_STEP_LABELS,
  enabledRfcForm,
  wizardPrefillFromRfc,
} from '@internal/components/group-build-wizard/utils';

export function GroupBuildWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {toast} = useToast();

  const onboardingId = searchParams.get('onboardingId');
  const stepId = searchParams.get('stepId');
  const hasOnboardingContext = !!onboardingId && !!stepId;

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [state, setState] = useState<WizardState>({
    addressLine1: '',
    addressLine2: '',
    billingCycleId: 'MO',
    city: '',
    email: '',
    federalTaxId: '',
    firstName: '',
    groupName: '',
    isAnalyzerGroup: true,
    lastName: '',
    originalDate: '',
    openEnrollmentDate: '',
    phone: '',
    selectedAgent: null,
    selectedPlanVariationIds: [],
    selectedVbaPlanIds: [],
    stateCode: '',
    zipCode: '',
  });
  const [prefilled, setPrefilled] = useState(false);

  const onboardingQuery = trpc.internal.onboarding.getOnboarding.useQuery(
    {onboardingId: onboardingId!},
    {enabled: hasOnboardingContext},
  );

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    enabled: !hasOnboardingContext,
  });

  type OnboardingData = NonNullable<RouterOutput['internal']['onboarding']['getOnboarding']>;
  const rfcSubmission = onboardingQuery.data?.rfcSubmission as OnboardingData['rfcSubmission'];

  const clientId = onboardingQuery.data?.clientId ?? selectedClientId;

  useEffect(() => {
    if (prefilled || !onboardingQuery.data) return;

    const updates = wizardPrefillFromRfc(enabledRfcForm(rfcSubmission));

    if (!updates.groupName && rfcSubmission?.companyName) {
      updates.groupName = rfcSubmission.companyName;
    }

    if (Object.keys(updates).length > 0) {
      setState((prev) => ({...prev, ...updates}));
    }
    setPrefilled(true);
  }, [onboardingQuery.data, prefilled]);

  function updateState(partial: Partial<WizardState>) {
    setState((prev) => ({...prev, ...partial}));
  }

  const createGroupForOnboardingMutation =
    trpc.internal.onboarding.createGroupForOnboarding.useMutation({
      onSuccess: (data) => {
        toast({title: 'Group created', description: `VBA Group ID: ${data.groupId}`});
        if (onboardingId) {
          navigate(`/onboarding/${onboardingId}`);
        }
      },
      onError: createMutationErrorHandler('Failed to create group'),
    });

  const createGroupStandaloneMutation = trpc.internal.onboarding.createGroupStandalone.useMutation({
    onSuccess: (data) => {
      toast({title: 'Group created', description: `VBA Group ID: ${data.groupId}`});
      navigate(`/groups/${data.groupId}`);
    },
    onError: createMutationErrorHandler('Failed to create group'),
  });

  const isSubmitting =
    createGroupForOnboardingMutation.isPending || createGroupStandaloneMutation.isPending;

  function handleSubmit() {
    const groupPayload = {
      address: {
        addressLine1: state.addressLine1.trim(),
        addressLine2: state.addressLine2.trim() || null,
        city: state.city.trim(),
        stateCode: state.stateCode.trim(),
        zipCode: state.zipCode.trim() || null,
      },
      agentId: state.selectedAgent?.admin123AgentId || undefined,
      billingCycleId: state.billingCycleId,
      commissionAgentId: state.selectedAgent?.id,
      federalTaxId: state.federalTaxId.trim() || null,
      groupName: state.groupName.trim(),
      isAnalyzerGroup: state.isAnalyzerGroup,
      originalDate: state.originalDate,
      openEnrollmentDate: state.openEnrollmentDate || null,
      planVariationIds:
        state.selectedPlanVariationIds.length > 0 ? state.selectedPlanVariationIds : undefined,
      vbaPlanIds: state.selectedVbaPlanIds.length > 0 ? state.selectedVbaPlanIds : undefined,
      primaryContact: {
        email: state.email.trim(),
        firstName: state.firstName.trim(),
        lastName: state.lastName.trim(),
        phoneNumber: state.phone.trim(),
      },
    };

    if (hasOnboardingContext) {
      createGroupForOnboardingMutation.mutate({
        onboardingId: onboardingId!,
        stepId: stepId!,
        ...groupPayload,
      });
    } else {
      createGroupStandaloneMutation.mutate({
        clientId,
        ...groupPayload,
      });
    }
  }

  const isGroupInfoValid = useMemo(
    () =>
      !!(
        clientId &&
        state.groupName.trim() &&
        state.addressLine1.trim() &&
        state.city.trim() &&
        state.stateCode.trim() &&
        state.firstName.trim() &&
        state.lastName.trim() &&
        state.phone.trim() &&
        state.email.trim() &&
        state.originalDate
      ),
    [state, clientId],
  );

  const backTarget = hasOnboardingContext ? `/onboarding/${onboardingId}` : '/groups';

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='px-8 pt-6 pb-4 max-w-3xl mx-auto w-full'>
        <button
          onClick={() => navigate(backTarget)}
          className='flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-navy transition-colors mb-6'
        >
          <ChevronLeft className='h-4 w-4' />
          {hasOnboardingContext ? 'Back to Onboarding' : 'Back to Groups'}
        </button>

        <h1 className='text-2xl font-bold text-brand-navy mb-1'>Build Group</h1>
        <p className='text-sm text-muted-foreground'>
          {hasOnboardingContext
            ? `Creating group for: ${onboardingQuery.data?.groupName ?? '...'}`
            : 'Create a new group in VBA'}
        </p>
      </div>

      <StepIndicator steps={WIZARD_STEP_LABELS} currentStep={currentStep} />

      <div className='px-8 py-6 max-w-3xl mx-auto w-full'>
        {currentStep === 0 && (
          <StepAgentLookup state={state} onUpdate={updateState} onNext={() => setCurrentStep(1)} />
        )}
        {currentStep === 1 && (
          <StepGroupInfo
            state={state}
            onUpdate={updateState}
            clientId={clientId}
            onClientIdChange={setSelectedClientId}
            clients={clientsQuery.data ?? []}
            showClientSelector={!hasOnboardingContext}
            isValid={isGroupInfoValid}
            onBack={() => setCurrentStep(0)}
            onNext={() => setCurrentStep(2)}
          />
        )}
        {currentStep === 2 && (
          <StepProductTemplate
            state={state}
            onUpdate={updateState}
            clientId={clientId}
            onBack={() => setCurrentStep(1)}
            onNext={() => setCurrentStep(3)}
          />
        )}
        {currentStep === 3 && (
          <StepReview
            state={state}
            clientId={clientId}
            isValid={isGroupInfoValid}
            isSubmitting={isSubmitting}
            onBack={() => setCurrentStep(2)}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  );
}
